// Fills the real DWP Attendance Allowance PDF entirely client-side with pdf-lib,
// reusing KareHero's existing question-id -> PDF-field mapping.
//
// This is a TypeScript port of the backend pdf-generator semantics
// (backend/pdf-generator/src/mapper.py + pdf.py), validated field-by-field
// against the normalized form. The bundled PDF at /aa/attendance-allowance.pdf
// has been decrypted + decompressed offline (see scripts/normalize-aa-pdf.py)
// because the original gov PDF is encrypted with object streams that pdf-lib
// cannot parse.

import { PDFDocument, PDFName } from 'pdf-lib';
import mappingJson from './mapping.json';
import { fieldsById } from './aaSchema';
import type {
  AnswerSet,
  AnswerValues,
  FieldMapping,
  FieldMappings,
} from './types';

const mapping = mappingJson as unknown as FieldMappings;
const PDF_URL = '/aa/attendance-allowance.pdf';

type PdfWrite =
  | { kind: 'text'; text: string }
  | { kind: 'checkbox'; on: boolean }
  | { kind: 'name'; export: string };

/** Resolve the care-form answer for a mapping entry (mirrors mapper.py). */
function resolveValue(m: FieldMapping, answers: AnswerValues): unknown {
  if (!(m.questionID in answers)) return undefined;
  let answer: any = answers[m.questionID];

  // table rows: answer is an array, pick the row at `index`
  if (m.index !== undefined && m.index !== null) {
    if (!Array.isArray(answer) || answer.length <= m.index) return undefined;
    answer = answer[m.index];
  }

  // composite fields (e.g. address): join named sub-keys, or read a single bool key
  if (Array.isArray(m.answerKeys) && m.answerKeys.length) {
    if (m.fieldType === 'checkbox') {
      answer =
        answer && m.answerKeys[0] in answer ? answer[m.answerKeys[0]] : false;
    } else {
      answer = m.answerKeys
        .filter((k) => answer && k in answer && String(answer[k]).length > 0)
        .map((k) => String(answer[k]))
        .join(', ');
    }
  }

  switch (m.fieldType) {
    case 'select':
      for (const o of m.options ?? []) if (o.answer === answer) return o.value;
      return undefined;
    case 'multi-select':
      for (const o of m.options ?? [])
        if (Array.isArray(answer) && answer.includes(o.answer)) return o.value;
      return undefined;
    case 'textfield': {
      // mapper.py nuance: a textfield that was presented as multiple choice
      // stores the option id; the PDF wants the human-readable label
      // (e.g. "nationality-british" -> "British").
      const opts = fieldsById[m.questionID]?.options;
      if (opts) for (const o of opts) if (o.id === answer) return o.label;
      return answer;
    }
    default:
      return answer;
  }
}

/** Map a resolved value to a concrete PDF write (mirrors pdf.py::map_to_pdf_value). */
function toPdfWrite(
  fieldType: FieldMapping['fieldType'],
  value: unknown,
): PdfWrite | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  switch (fieldType) {
    case 'checkbox': {
      // Stringified booleans reach here via table-cell edits; Boolean('false')
      // would tick the box that the Python reference leaves unticked.
      const on =
        typeof value === 'string'
          ? !['false', 'no', 'off', '0', ''].includes(
              value.trim().toLowerCase(),
            )
          : Boolean(value);
      return { kind: 'checkbox', on };
    }
    case 'textfield':
    case 'question-table':
      return { kind: 'text', text: String(value) };
    case 'select':
    case 'multi-select':
      return { kind: 'name', export: String(value).replace(/^\//, '') };
    case 'date': {
      const mt = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!mt) return undefined;
      // Leave impossible calendar dates blank (the backend's strptime does too).
      const [y, mo, d] = [Number(mt[1]), Number(mt[2]), Number(mt[3])];
      const dt = new Date(Date.UTC(y, mo - 1, d));
      if (
        dt.getUTCFullYear() !== y ||
        dt.getUTCMonth() !== mo - 1 ||
        dt.getUTCDate() !== d
      )
        return undefined;
      return { kind: 'text', text: `${mt[3]}/${mt[2]}/${mt[1]}` };
    }
    default:
      return undefined;
  }
}

/** Set a select/radio-style field (implemented as keyed checkbox widgets) to an export value. */
function setNameField(form: any, fieldName: string, exportVal: string): void {
  const field = form.getField(fieldName);
  const acro = field.acroField;
  // Match widgets by PDFName identity, not by asString(): asString() returns the
  // #-escaped encoded form (e.g. /Twice#20a#20night), so any export value with a
  // space or non-ASCII char would never match and the tick would not display.
  // PDFName.of pools instances by decoded value, so identity comparison is exact;
  // writing the widget's own on-value also sidesteps encoding differences.
  const target = PDFName.of(exportVal);
  let matchedOn: typeof target | undefined;
  for (const w of acro.getWidgets()) {
    let on;
    try {
      on = w.getOnValue();
    } catch {
      /* widget has no on-value */
    }
    if (on && on === target) {
      matchedOn = on;
      break;
    }
  }
  const value = matchedOn ?? target;
  acro.dict.set(PDFName.of('V'), value);
  for (const w of acro.getWidgets()) {
    let on;
    try {
      on = w.getOnValue();
    } catch {
      /* widget has no on-value */
    }
    w.dict.set(PDFName.of('AS'), on && on === value ? on : PDFName.of('Off'));
  }
}

/** Flatten an AnswerSet (with metadata) down to plain questionId -> value. */
export function valuesFromAnswerSet(set: AnswerSet): AnswerValues {
  const out: AnswerValues = {};
  for (const [qid, fa] of Object.entries(set)) {
    if (fa.value !== null && fa.value !== undefined) out[qid] = fa.value;
  }
  return out;
}

export interface FillResult {
  bytes: Uint8Array;
  filledCount: number;
}

/** Load the bundled normalized PDF and fill it from the given answers. */
export async function fillAaPdf(answers: AnswerValues): Promise<FillResult> {
  const src = await fetch(PDF_URL).then((r) => {
    if (!r.ok) throw new Error(`Failed to load AA form PDF (${r.status})`);
    return r.arrayBuffer();
  });
  const pdf = await PDFDocument.load(src, {
    ignoreEncryption: true,
    throwOnInvalidObject: false,
    updateMetadata: false,
  });
  const form = pdf.getForm();
  const present = new Set(form.getFields().map((f) => f.getName()));

  let filledCount = 0;
  for (const [pdfField, m] of Object.entries(mapping)) {
    if (!present.has(pdfField)) continue;
    const write = toPdfWrite(m.fieldType, resolveValue(m, answers));
    if (!write) continue;
    try {
      if (write.kind === 'text') {
        form.getTextField(pdfField).setText(write.text);
      } else if (write.kind === 'checkbox') {
        const cb = form.getCheckBox(pdfField);
        write.on ? cb.check() : cb.uncheck();
      } else {
        setNameField(form, pdfField, write.export);
      }
      filledCount += 1;
    } catch {
      // Skip fields that cannot be set; a partial fill is expected and fine.
    }
  }

  const bytes = await pdf.save({ updateFieldAppearances: true });
  return { bytes, filledCount };
}

/** Fill the form and trigger a browser download. */
export async function downloadFilledAaPdf(
  answers: AnswerValues,
  filename = 'attendance-allowance-claim.pdf',
): Promise<FillResult> {
  const result = await fillAaPdf(answers);
  const blob = new Blob([result.bytes as BlobPart], {
    type: 'application/pdf',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return result;
}
